import { useCallback, useContext, useEffect } from 'react';
import { useOSPatchSilence } from 'customers/db-services-common-view/components/DBServices/useOSPatchSilence';
import { evaluateTenantName } from 'common/TenantContainer/evaluateTenantName';
import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';

type ServiceData = {
  id?: string;
  tenantId?: string;
  tenantDomain?: string;
  [key: string]: any;
};

type UseOSPatchSilenceModalProps = {
  services: ServiceData[];
  onSuccess?: () => void;
  onError?: (error: any) => void;
  computeResourceId?: string;
  tenantId?: string;
  tenantDomain?: string;
};

export const useOSPatchSilenceModal = ({
  services,
  onSuccess,
  onError,
  computeResourceId,
  tenantId,
  tenantDomain,
}: UseOSPatchSilenceModalProps) => {
  const {
    submitSilence,
    silenceIsLoading,
    silenceResult,
    createSilenceRequest,
  } = useOSPatchSilence();

  const { addNotification } = useContext(NotificationContext);

  const handleSilence = useCallback(
    async (startsAt?: string, endsAt?: string) => {
      try {
        // Extract service IDs and tenant information from the services data
        const serviceIds = services
          .map((service) => service.id)
          .filter(Boolean) as string[];

        // Use data from the first service for tenant info (assuming all services belong to same tenant)
        const firstService = services?.length > 0 ? services[0] : null;

        // Extract tenant information from service data using correct property paths
        let domain = tenantDomain;

        if (firstService) {
          domain =
            firstService?.service?.pgSource || firstService?.pgSource || '';

          // If we have tenant object, use evaluateTenantName as fallback
          if (firstService.tenant && !domain) {
            domain = evaluateTenantName({ tenant: firstService.tenant });
          }
        }

        if (!tenantId || !domain) {
          throw new Error(
            `Missing tenant information. TenantId: "${tenantId}", Domain: "${domain}". Available properties: ${
              firstService ? Object.keys(firstService).join(', ') : 'none'
            }`,
          );
        }

        // Create the request payload with the selected times
        if (!startsAt || !endsAt) {
          throw new Error('Start and end times are required');
        }

        const requestData = {
          tenantId,
          domain,
          silencePayload: {
            startsAt,
            endsAt,
            ...(computeResourceId && { computeResourceId }),
            serviceIds: serviceIds.join(','),
          },
        };

        // Submit the API call
        await submitSilence(JSON.stringify(requestData));
      } catch (error) {
        console.error('Failed to silence OS patch:', error);
        onError?.(error);
        throw error;
      }
    },
    [
      services,
      submitSilence,
      createSilenceRequest,
      onError,
      tenantId,
      tenantDomain,
      computeResourceId,
    ],
  );

  // Handle successful API response
  useEffect(() => {
    if (silenceResult.success && silenceResult.response) {
      // Show success toast notification
      addNotification({
        message:
          'OS patch notifications have been successfully silenced for the selected services.',
        severity: 'success',
      });

      onSuccess?.();
    }
  }, [silenceResult, onSuccess, addNotification]);

  return {
    handleSilence,
    isLoading: silenceIsLoading,
    silenceResult,
  };
};
